import { useToast } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useClient } from 'hooks/useClient';
import { IBaseAuthentication, IUserTeam } from '@cincel/client';
import { useRouter } from 'hooks/useRouter';
import { useSession } from 'store/session';
import { ERROR_TOAST_DEFAULTS, SUCCESS_TOAST_DEFAULTS } from '@helpers';
import { translations } from 'translations';
import fp from 'lodash/fp';
import jwt_decode from 'jwt-decode';

import { useMixpanel } from 'hooks/useMixpanel';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { LOGIN_CODES } from './helpers';
import { useLegacyOrganizations } from 'hooks/useLegacyOrganizations';
import { resetInactivityTimeout } from 'utils/session';
import {
  setSessionTokenV3,
  setLegacyToken,
  setSessionUser,
  getStorage
} from 'helpers/sessionContext';

const tmpUserEmail = 'cincelTmpEmail';

export const useLogin = () => {
  const v2 = useClient({ url: process.env.REACT_APP_API, useAuth: false });
  const v3 = useClient({ url: process.env.REACT_APP_API_V3, useAuth: false });

  const toast = useToast();

  const { t } = useTranslation();

  const router = useRouter();

  const { trackEvent } = useMixpanel();

  const { setIsLoggedIn, setTokenV3, setToken, setUser, setTeam, clean, setIsV3Expired } =
    useSession();

  const mapLegacyOrganizationsFn = useLegacyOrganizations();

  const pickTeam = (teams) => {
    if (!teams || !teams.length) {
      return {} as IUserTeam;
    }

    const defaultTeam = teams.find((item) => item?.is_default_team === true);
    return defaultTeam || teams[0];
  }

  const fn = async (payload: IBaseAuthentication & { skipRedirect?: boolean }) => {
    getStorage().setItem(tmpUserEmail, payload.email);

    let dataV3;
    let dataV2: any;

    // "Shotgun" Approach: Try everything in parallel.
    // We don't care if input is Password or OTP, we try all meaningful endpoints.
    // V3 is smart enough to handle both. V2 endpoints are specific, so we try both.

    const secret = payload.password; // Can be password or OTP code

    const [v3Result, v2Result] = await Promise.allSettled([
      // 1. V3 Login (The Source of Truth)
      v3.getV3Token({
        username: payload.email,
        password: secret,
      }),
      // 2. Legacy V2 Login (Smart handling internal in client)
      v2.login({ email: payload.email, password: secret } as any),
    ]);

    // Process V3 Result
    if (v3Result.status === 'fulfilled') {
      dataV3 = v3Result.value;
    } else {
      console.warn('V3 login attempt failed', v3Result.reason);
    }

    // Process V2 Result
    if (v2Result.status === 'fulfilled') {
      dataV2 = v2Result.value;
    } else {
      console.warn('Legacy V2 login attempt failed', v2Result.reason);
    }

    // Validation:
    // V3 is the source of truth (97% of experience). 
    // If V3 fails, the login is considered failed, regardless of V2 status.
    if (!dataV3) {
      if (v3Result.status === 'rejected') {
        throw v3Result.reason;
      }
      throw new Error('V3_LOGIN_FAILED');
    }

    const msg = t(fp.get(201, LOGIN_CODES));
    toast({ ...SUCCESS_TOAST_DEFAULTS, title: msg });

    if (dataV2?.token) {
      setLegacyToken(dataV2.token);
    }
    // dataV3 might be undefined if V3 failed in parallel execution, but we check above
    if (dataV3) {
      setSessionTokenV3(dataV3);
    }

    // If V2 failed, we use fallback user data
    if (dataV2?.user) {
      setSessionUser(dataV2?.user);
    } else {
      // Fallback: Construct minimal user object from V3 token
      let v3UserId = 'otp-user';
      try {
        const decoded: any = jwt_decode(dataV3);
        v3UserId = decoded?.id || decoded?.sub || v3UserId;
      } catch (e) {
        console.warn('Failed to decode V3 token for fallback user', e);
      }

      const fallbackUser = { email: payload.email, id: v3UserId };
      setSessionUser(fallbackUser);
    }

    trackEvent('Log In', {
      email: dataV2?.user?.email || payload.email,
      socialLogin: false,
    });

    /**
     * Fetch and set default team.
     */
    v3.setAuthorizationHeader(dataV3);

    let userId = null;

    // Prioritize ID from V3 token (JWT) as we want to deprecate V2
    if (dataV3) {
      try {
        const decoded: any = jwt_decode(dataV3);
        userId = decoded?.id || decoded?.sub;
      } catch (e) {
        console.error('Could not decode V3 token', e);
      }
    }

    // Fallback to V2 ID only if V3 decoding failed
    if (!userId) {
      userId = dataV2?.user?.id;
    }

    if (!userId) {
      throw new Error('NO_USER_ID_FOUND');
    }

    if (userId) {
      try {
        const axios = v3.getAxiosInstance();
        // Use the numeric ID instead of 'me' for consistency
        const { data: profile } = await axios.get<any>(`/users/${userId}`);
        if (profile) {
          setSessionUser(profile);
          setUser(profile);

          // REDIRECTION LOGIC:
          // If the profile indicates the user should be in PRO (isPro: true)
          // and the target host is different from current, we redirect immediately.
          if (profile.isPro && profile.host && profile.host !== window.location.host) {
            window.location.href = `https://${profile.host}/auth/login?token=${dataV3}`;
            return;
          }
        }
      } catch (e) {
        console.error('Could not fetch user profile from V3', e);
      }
    }

    const { data: teams } = await v3.getUserTeams(userId as any);
    const defaultTeam = pickTeam(teams);
    setTeam(defaultTeam);

    if (dataV2?.user?.id) {
      await mapLegacyOrganizationsFn(
        dataV2?.user?.id,
        defaultTeam?.uuid,
        dataV2?.user?.organizations,
        dataV2?.token,
      );
    }

    setTokenV3(dataV3);
    if (dataV2?.token) {
      setToken(dataV2.token);
    }

    if (dataV2?.user) {
      setUser(dataV2.user);
    }

    // Reset activity timer and expired state on successful login
    setIsV3Expired(false);
    resetInactivityTimeout();

    setIsLoggedIn(true);

    if (!payload.skipRedirect) {
      router.history.push('/');
    }

    getStorage().removeItem(tmpUserEmail);
  };

  const onError = useCallback(
    (error: AxiosError | Error) => {
      const isCustomError = error instanceof Error && error.message === 'NO_USER_ID_FOUND';
      const defaultErr = translations.ERRORS.NETWORK_DEFAULT;
      const code = (error as AxiosError)?.response?.status as number;

      let msg = t(fp.getOr(defaultErr)(code, LOGIN_CODES));

      if (isCustomError) {
        msg = 'No se pudo validar tu identidad. Por favor intenta de nuevo.';
      }

      toast({ ...ERROR_TOAST_DEFAULTS, title: msg });

      if (isCustomError) {
        clean();
        getStorage().clear();
        router.history.push('/login');
        return;
      }

      // TODO: Maybe add an axios handler for this specific case.
      if (code === 403) {
        const email = getStorage().getItem(tmpUserEmail) as string;
        router.history.push({
          pathname: '/confirmation',
          search: `?email=${encodeURIComponent(email)}`,
          state: { sendCodeEnabled: true },
        });
      }
    },
    [clean, router.history, t, toast],
  );

  const mutation = useMutation(['post-login'], fn, { onError });

  return { ...mutation };
};
